import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DayPicker, type SelectRangeEventHandler } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
  computePresetRange,
  formatMmDdYyyy,
  parseIsoLocal,
  PRESET_LABELS,
  toYmdLocal,
  VISIBLE_PRESETS,
  type DateRangePreset,
} from '../../../utils/dateRange';

export interface DateRangeValue {
  preset: DateRangePreset;
  start: string;
  end: string;
}

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
}

// Ported from frontend/src/components/shared/DateRangeSelector.tsx so the
// publisher Overview reads as a sibling of the internal dashboard's Self-
// Serve tab: a pill button showing the MM/DD/YYYY - MM/DD/YYYY range that
// opens a two-month calendar popover for custom ranges, plus preset pills
// (Yesterday, L7D, L30D, MTD, LM, YTD). Constrains to past dates only
// because delivery data doesn't exist for future days.
const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (popoverOpen && popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [popoverOpen]);

  const applyPreset = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      onChange({ ...value, preset });
      setPopoverOpen(true);
      return;
    }
    const { start, end } = computePresetRange(preset, new Date());
    onChange({ preset, start, end });
  };

  const handleCalendarSelect: SelectRangeEventHandler = (selected) => {
    if (!selected?.from || !selected?.to) return;
    onChange({
      preset: 'custom',
      start: toYmdLocal(selected.from),
      end: toYmdLocal(selected.to),
    });
  };

  const today = new Date();
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative" ref={popoverRef}>
        <button
          type="button"
          onClick={() => setPopoverOpen((open) => !open)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white flex items-center gap-2 hover:border-slate-300"
        >
          <span className="text-slate-700">
            {formatMmDdYyyy(value.start)}
            <span className="mx-1 text-slate-400">–</span>
            {formatMmDdYyyy(value.end)}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-500" />
        </button>
        {popoverOpen && (
          <div className="absolute z-30 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 p-3">
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={{ from: parseIsoLocal(value.start), to: parseIsoLocal(value.end) }}
              onSelect={handleCalendarSelect}
              defaultMonth={parseIsoLocal(value.end)}
              disabled={[{ from: todayLocal, to: new Date(8640000000000000) }]}
              className="rdp-root"
              style={{
                '--rdp-accent-color': '#2563eb',
                '--rdp-accent-color-dark': '#1d4ed8',
                '--rdp-range-middle-background-color': '#2563eb',
                '--rdp-range-start-color': '#ffffff',
                '--rdp-range-end-color': '#ffffff',
                '--rdp-range-middle-color': '#ffffff',
              } as React.CSSProperties}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {VISIBLE_PRESETS.map((preset) => {
          const active = value.preset === preset;
          return (
            <button
              key={preset}
              type="button"
              onClick={() => applyPreset(preset)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                active
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {PRESET_LABELS[preset]}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DateRangeSelector;
